import React, { useState, useRef, useCallback, useEffect } from 'react';
import { searchMentionableMembers } from '../../services/mention.service';

/**
 * A contenteditable input with @mention autocomplete — typing "@" followed
 * by a name shows a dropdown of real active members; picking one inserts a
 * real chip showing just "@Name". Same value/onChange contract as before
 * (a plain string using the stored marker format, "@[Name](memberId)"),
 * so no caller needs to change — only how it LOOKS while composing changed.
 *
 * Previously this was a plain <textarea>/<input>, and insertMention() wrote
 * the literal "@[Name](memberId) " marker text straight into the visible
 * value — meaning the raw 36-character member id sat right there in the box
 * for as long as you kept composing, only ever getting cleaned up once
 * rendered elsewhere via renderRichText/stripMentionMarkers. Jenn,
 * 2026-08-31: "Is there a reason why the unique member id... shows and
 * stays present when someone is tagged? Let's not have that show up." A
 * plain textarea has no way to show a styled chip for part of its value
 * while keeping the rest as plain text — hence contenteditable, the same
 * approach RichTextEditor.jsx already uses for the same underlying reason.
 */
const MENTION_PATTERN = /@\[([^\]]+)\]\(([0-9a-f-]{36})\)/g;

const escapeHtml = (s) => s
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const CHIP_CLASS = 'bg-reset-terracotta/10 text-reset-terracotta rounded px-1 font-medium';

/** Marker-format string -> HTML string, mention tags become non-editable chips. */
const markersToHtml = (text) => {
  if (!text) return '';
  let html = '';
  let lastIndex = 0;
  let m;
  MENTION_PATTERN.lastIndex = 0;
  while ((m = MENTION_PATTERN.exec(text))) {
    html += escapeHtml(text.slice(lastIndex, m.index)).replace(/\n/g, '<br>');
    html += `<span contenteditable="false" data-mention-id="${m[2]}" class="${CHIP_CLASS}">@${escapeHtml(m[1])}</span>`;
    lastIndex = m.index + m[0].length;
  }
  html += escapeHtml(text.slice(lastIndex)).replace(/\n/g, '<br>');
  return html;
};

/** contenteditable root -> marker-format string, chips become "@[Name](id)". */
const nodesToMarkers = (root) => {
  let result = '';
  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) { result += node.textContent; return; }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    if (node.tagName === 'BR') { result += '\n'; return; }
    if (node.dataset?.mentionId) { result += `@[${node.textContent.replace(/^@/, '')}](${node.dataset.mentionId})`; return; }
    if (node.tagName === 'DIV') { result += '\n'; Array.from(node.childNodes).forEach(walk); return; }
    Array.from(node.childNodes).forEach(walk);
  };
  Array.from(root.childNodes).forEach(walk);
  return result.replace(/^\n/, '');
};

export default function MentionInput({ value, onChange, placeholder, multiline = true, rows = 3, className }) {
  const [matches, setMatches] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const elRef = useRef(null);
  const debounceRef = useRef(null);
  // Range where the "@partial" query text currently sits, so insertMention
  // knows exactly what to delete-and-replace with the chip.
  const queryRangeRef = useRef(null);
  const lastEmitted = useRef(Symbol('unset'));

  useEffect(() => {
    if (value === lastEmitted.current) return;
    if (elRef.current) elRef.current.innerHTML = markersToHtml(value);
    lastEmitted.current = value;
  }, [value]);

  const emitChange = () => {
    if (!elRef.current) return;
    const next = nodesToMarkers(elRef.current);
    lastEmitted.current = next;
    onChange(next);
  };

  // Looks at the text node containing the cursor for a trailing "@partial"
  // — same idea as the old regex-on-a-plain-string version, just scoped to
  // the current text node instead of the whole value.
  const findMentionQuery = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    if (!range.collapsed || range.startContainer.nodeType !== Node.TEXT_NODE) return null;
    const text = range.startContainer.textContent.slice(0, range.startOffset);
    const match = text.match(/(?:^|\s)@([a-zA-Z0-9 ]{0,30})$/);
    if (!match) return null;
    return { node: range.startContainer, start: range.startOffset - match[1].length - 1, end: range.startOffset, query: match[1] };
  };

  const handleInput = () => {
    emitChange();
    const mention = findMentionQuery();
    queryRangeRef.current = mention;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (mention) {
      debounceRef.current = setTimeout(async () => {
        try {
          const results = await searchMentionableMembers(mention.query);
          setMatches(results);
          setShowDropdown(results.length > 0);
          setActiveIndex(0);
        } catch { setShowDropdown(false); }
      }, 150);
    } else {
      setShowDropdown(false);
    }
  };

  const insertMention = useCallback((member) => {
    const mention = queryRangeRef.current;
    const el = elRef.current;
    if (!mention || !el) return;

    // Replace the "@partial" text (start..end within that text node) with a
    // real chip element, then a trailing space to keep typing outside it.
    const range = document.createRange();
    range.setStart(mention.node, mention.start);
    range.setEnd(mention.node, mention.end);
    range.deleteContents();

    const chip = document.createElement('span');
    chip.contentEditable = 'false';
    chip.dataset.mentionId = member.id;
    chip.className = CHIP_CLASS;
    chip.textContent = `@${member.name}`;

    const space = document.createTextNode(' ');

    // One insertNode call with both nodes in a fragment, not two separate
    // calls — Range.insertNode's exact resulting boundary position after an
    // insert isn't reliable enough to chain safely, so a second insertNode
    // could land before or after the first depending on the browser.
    const frag = document.createDocumentFragment();
    frag.appendChild(chip);
    frag.appendChild(space);
    range.insertNode(frag);

    const sel = window.getSelection();
    sel.removeAllRanges();
    const after = document.createRange();
    after.setStartAfter(space);
    after.collapse(true);
    sel.addRange(after);

    setShowDropdown(false);
    queryRangeRef.current = null;
    el.focus();
    emitChange();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleKeyDown = (e) => {
    if (showDropdown) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, matches.length - 1)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(matches[activeIndex]); return; }
      if (e.key === 'Escape') { setShowDropdown(false); return; }
    }
    if (!multiline && e.key === 'Enter') {
      e.preventDefault();
      elRef.current?.closest('form')?.requestSubmit();
    }
  };

  return (
    <div className="relative">
      <div
        ref={elRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
        data-placeholder={placeholder}
        style={{ minHeight: multiline ? `${rows * 1.4}em` : undefined, whiteSpace: multiline ? 'pre-wrap' : 'nowrap', overflow: multiline ? undefined : 'hidden' }}
        className={`${className} empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400`}
      />
      {showDropdown && (
        <div className="absolute z-20 mt-1 w-full max-w-xs bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {matches.map((m, i) => (
            <button
              key={m.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); insertMention(m); }}
              className={`w-full text-left px-3 py-2 text-sm ${i === activeIndex ? 'bg-reset-cream text-reset-brown' : 'text-gray-600 hover:bg-reset-cream'}`}
            >
              {m.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
